import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchAllChannelsData, YouTubeStats, YouTubeVideo, CHANNELS } from '../services/youtube';
import { trackClick, trackEvent } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  BackButton,
  Header,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
  StatsGrid,
  StatCell,
  StatValue,
  StatLabel,
} from '../components/minimal';

const VIDEOS_ACCENT = '#ef4444';

const WideInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 5rem 2rem 6rem;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem 4rem;
  }
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 2rem;
  padding: 0.25rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: ${p => (p.$active ? 'rgba(255, 255, 255, 0.06)' : 'transparent')};
  border: none;
  color: ${p => (p.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.45)')};
  padding: 0.5rem 1rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const VideoCard = styled.button`
  text-align: left;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &:hover img {
    opacity: 0.85;
  }

  &:hover h3 {
    color: ${VIDEOS_ACCENT};
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.2s ease;
  }
`;

const DurationBadge = styled.span`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.85);
  color: #ffffff;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
`;

const VideoTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s ease;
`;

const VideoMeta = styled.div`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.45);
`;

const Status = styled.div`
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 3rem 0;
  font-size: 0.875rem;
`;

interface ChannelData {
  stats: YouTubeStats;
  videos: YouTubeVideo[];
}

const formatCount = (count: string | number): string => {
  const n = typeof count === 'string' ? parseInt(count, 10) : count;
  if (Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const parseDuration = (iso: string): string => {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return iso;
  const h = parseInt(match[1] || '0', 10);
  const m = parseInt(match[2] || '0', 10);
  const s = parseInt(match[3] || '0', 10);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const VideosPage: React.FC = () => {
  const [musicData, setMusicData] = useState<ChannelData | null>(null);
  const [personalData, setPersonalData] = useState<ChannelData | null>(null);
  const [activeChannel, setActiveChannel] = useState<'music' | 'personal'>('personal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allData = await fetchAllChannelsData();
        const sortByDate = (a: YouTubeVideo, b: YouTubeVideo) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        setMusicData({
          stats: allData.music.stats,
          videos: allData.music.videos.sort(sortByDate),
        });
        setPersonalData({
          stats: allData.personal.stats,
          videos: allData.personal.videos.sort(sortByDate),
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching YouTube data:', err);
        setError('Failed to load YouTube data.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const currentData = activeChannel === 'music' ? musicData : personalData;
  const currentConfig = activeChannel === 'music' ? CHANNELS.MUSIC : CHANNELS.PERSONAL;

  const handleVideoClick = (video: YouTubeVideo) => {
    trackClick('youtube_video', {
      video_title: video.title,
      video_id: video.id,
      video_duration: video.duration,
      channel_type: activeChannel,
    });
    window.open(`https://www.youtube.com/watch?v=${video.id}`, '_blank');
  };

  const handleChannelSwitch = (channel: 'music' | 'personal') => {
    setActiveChannel(channel);
    trackEvent('channel_switch', { from: activeChannel, to: channel });
  };

  return (
    <>
      <MinimalGlobalStyle $accent={VIDEOS_ACCENT} />
      <Container>
        <BackButton />
        <WideInner>
          <Header>
            <PageTitle>Videos</PageTitle>
            <Bio>Two YouTube channels — personal (tech, builder content) and music.</Bio>
            <HeaderLinks>
              <HeaderLink
                href={`https://www.youtube.com/${CHANNELS.PERSONAL.handle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Personal channel
              </HeaderLink>
              <HeaderLink
                href={`https://www.youtube.com/${CHANNELS.MUSIC.handle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Music channel
              </HeaderLink>
            </HeaderLinks>
          </Header>

          <TabRow>
            <Tab
              $active={activeChannel === 'personal'}
              disabled={!personalData}
              onClick={() => handleChannelSwitch('personal')}
            >
              Personal
            </Tab>
            <Tab
              $active={activeChannel === 'music'}
              disabled={!musicData}
              onClick={() => handleChannelSwitch('music')}
            >
              Music
            </Tab>
          </TabRow>

          {loading && <Status>Loading videos…</Status>}
          {error && <Status>{error}</Status>}

          {currentData && (
            <>
              <StatsGrid>
                <StatCell>
                  <StatValue>{formatCount(currentData.stats.subscriberCount)}</StatValue>
                  <StatLabel>Subscribers</StatLabel>
                </StatCell>
                <StatCell>
                  <StatValue>{formatCount(currentData.stats.viewCount)}</StatValue>
                  <StatLabel>Total views</StatLabel>
                </StatCell>
                <StatCell>
                  <StatValue>{currentData.stats.videoCount}</StatValue>
                  <StatLabel>Videos</StatLabel>
                </StatCell>
              </StatsGrid>

              <Section>
                <SectionTitle>{currentConfig.name}</SectionTitle>
                <VideoGrid>
                  {currentData.videos.map(video => (
                    <VideoCard key={video.id} onClick={() => handleVideoClick(video)}>
                      <Thumb>
                        <img src={video.thumbnail} alt={video.title} loading="lazy" />
                        <DurationBadge>{parseDuration(video.duration)}</DurationBadge>
                      </Thumb>
                      <VideoTitle>{video.title}</VideoTitle>
                      <VideoMeta>
                        {formatCount(video.viewCount)} views · {formatDate(video.publishedAt)}
                      </VideoMeta>
                    </VideoCard>
                  ))}
                </VideoGrid>
              </Section>
            </>
          )}
        </WideInner>
      </Container>
    </>
  );
};

export default VideosPage;
